import { usePerformRasnActionMutation, useSearchRasnsQuery } from 'api/rasn'
import { AuthRole } from 'auth/const'
import { CloseRasnIcon, RetriggerRasnEvent } from 'components/icon'
import { RetriggerRasnEventsModal } from 'components/retrigger-rasn-events-modal'
import { type ConfirmationConfig, TableView } from 'components/tableview'
import { ColumnType } from 'components/tableview/Filter'
import { usePaginatedData } from 'components/tableview/usePaginatedData'
import { useTableState } from 'components/tableview/useTableState'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { showSnackbar } from 'redux/slices/globalState'
import { dispatch, useAppSelector } from 'redux/store'
import { type QueryResult, type Rasn, RasnChannel, RasnStatus } from 'types'
import { getEnumValues } from 'utils/utils'

const FILTER_INPUT_DELAY_MS = 500

export const RasnOverview = () => {
  const user = useAppSelector((state) => state.globalState.user)
  const tableState = useTableState()
  const { data, loading } = usePaginatedData<QueryResult<Rasn>>(useSearchRasnsQuery, tableState.pagination, tableState.columnFilters, tableState.sorting)
  const navigate = useNavigate()
  const [performRasnAction] = usePerformRasnActionMutation()
  const [showRetriggerModal, setShowRetriggerModal] = useState(false)
  const [rasnId, setRasnId] = useState<string>('')

  const handleCloseRasn = async (rasn: Rasn) => {
    if (rasn.rasnFamilyId) {
      dispatch(showSnackbar({
        message: `RASN belongs to Family RSO ${rasn.rasnFamilyId}. Please close the Family RSO instead.`,
        status: 'error'
      }))
      return
    }
    const result = await performRasnAction({ rasnNumber: rasn.number, action: 'close' })
    if ('data' in result) {
      dispatch(showSnackbar({
        message: `RASN ${rasn.number} has been closed.`,
        status: 'success'
      }))
    }
  }

  const columns = useMemo(() => [
    { id: 'number', header: 'RASN', type: ColumnType.TEXT, columnWidth: 8 },
    { id: 'channel', header: 'Channel', type: ColumnType.ENUM, enumValues: getEnumValues(RasnChannel), columnWidth: 8 },
    { id: 'customerName', header: 'Customer Name', type: ColumnType.TEXT, columnWidth: 24 },
    { id: 'status', header: 'Status', type: ColumnType.ENUM, enumValues: getEnumValues(RasnStatus), columnWidth: 6 },
    { id: 'holdCode', header: 'Hold Code', type: ColumnType.BOOLEAN, columnWidth: 6 },
    { id: 'totalExpectedUnits', header: 'Expected', type: ColumnType.NUMBER, columnWidth: 6 },
    { id: 'totalHandledUnits', header: 'Handled', type: ColumnType.NUMBER, columnWidth: 6 },
    { id: 'created', header: 'Created', type: ColumnType.DATE, columnWidth: 12 },
    { id: 'lastModified', header: 'Last Modified', type: ColumnType.DATE, columnWidth: 12 }
  ], [])

  useEffect(() => {
    if (user) {
      setActionWithConfirmation(
        user.hasSpecific(AuthRole.MODIFY_RASN) ?
          [
            {
              title: 'Unlock RASN',
              message: 'Are you sure you want to unlock this RASN?',
              icon: 'Unlock',
              confirmText: 'Unlock',
              show: (row: Rasn) => !!row.holdCode,
              onClick: (row: Rasn) => { performRasnAction({ rasnNumber: row.number, action: 'unlock' }) }
            },
            {
              title: 'Close RASN',
              message: 'Are you sure you want to close this RASN?',
              icon: CloseRasnIcon,
              confirmText: 'Close',
              show: (row: Rasn) => row.status !== RasnStatus.CLOSED && row.status !== RasnStatus.CANCELLED,
              onClick: (row: Rasn) => { handleCloseRasn(row) }
            }
          ] : []
      )
    }
  }, [user])

  const [actionsWithConfirmation, setActionWithConfirmation] = useState<ConfirmationConfig[]>([])

  return (
    <>
      <TableView
        columns={columns}
        data={data?.results ?? []}
        rowCount={data?.totalCount ?? 0}
        tableState={tableState}
        textFilterDelay={FILTER_INPUT_DELAY_MS}
        loading={loading}
        actions={[
          {
            label: 'View',
            icon: 'Show',
            hoverText: 'RASN details',
            onClick: (row: Rasn) => { navigate(`/rasn/${row.number}`) }
          },
          {
            label: 'Retrigger Events',
            icon: RetriggerRasnEvent,
            hoverText: 'Retrigger RASN events',
            show: (row: Rasn) => (user ? user.hasSpecific(AuthRole.RETRIGGER_EVENTS) : false) && row.status === RasnStatus.CLOSED,
            onClick: (row: Rasn) => { setRasnId(row.number); setShowRetriggerModal(true) }
          }
        ]}
        actionsWithConfirmation={actionsWithConfirmation}
      />

      <RetriggerRasnEventsModal
        rasnNumber={rasnId}
        showModal={showRetriggerModal}
        setShowModal={setShowRetriggerModal}
      />
    </>
  )
}
